import { ClipboardList, FilePlus2, Globe, GitBranch, LayoutTemplate, Link2, ListChecks, MessagesSquare } from 'lucide-react';
import { canEdit } from '@nest-recruit/shared';
import type { router as AppRouter } from '../../router';
import { registerCommands, type Command } from '../../shell/command-registry';

/**
 * S11 and S12's palette entries (06-qol §1: every screen registers itself). Each names the
 * §L23 object it needs, so nobody is offered a destination they would be refused.
 *
 * "Jobs" itself is not here: the module bar already registers it. Starting a job from a
 * requisition lives on JOB-01, beside the approved requisitions it starts from.
 */
export function registerJobCommands(router: typeof AppRouter): () => void {
  const commands: Command[] = [
    {
      id: 'jobs.new',
      label: 'New job',
      section: 'action',
      context: 'F5.24 · create a job directly',
      permission: 'job_config',
      canSee: (session) => canEdit(session, 'job_config'),
      icon: FilePlus2,
      keywords: ['new job', 'create job', 'opening', 'vacancy', 'hire'],
      run: () => void router.navigate({ to: '/jobs/new' }),
    },
    {
      id: 'jobs.templates',
      label: 'Role templates',
      section: 'navigate',
      context: 'Admin · ADMIN-05',
      permission: 'hiring_setup',
      icon: LayoutTemplate,
      keywords: ['template', 'job', 'library', 'approve', 'role'],
      run: () => void router.navigate({ to: '/admin/role-templates' }),
    },
    {
      id: 'jobs.pipelines',
      label: 'Pipelines',
      section: 'navigate',
      context: 'Admin · ADMIN-01',
      permission: 'hiring_setup',
      icon: GitBranch,
      keywords: ['pipeline', 'stages', 'role family', 'candidate-facing'],
      run: () => void router.navigate({ to: '/admin/pipelines' }),
    },
    {
      id: 'jobs.rubrics',
      label: 'Scorecard rubrics',
      section: 'navigate',
      context: 'Admin · ADMIN-02',
      permission: 'hiring_setup',
      icon: ListChecks,
      keywords: ['rubric', 'scorecard', 'criteria', 'weights', 'critical', 'scoring'],
      run: () => void router.navigate({ to: '/admin/rubrics' }),
    },
    {
      id: 'jobs.interview-kits',
      label: 'Interview kits',
      section: 'navigate',
      context: 'Admin · ADMIN-04',
      permission: 'hiring_setup',
      icon: MessagesSquare,
      keywords: ['kit', 'questions', 'interview', 'rubric'],
      run: () => void router.navigate({ to: '/admin/interview-kits' }),
    },
    {
      id: 'jobs.interview-plans',
      label: 'Interview plans',
      section: 'navigate',
      context: 'Admin · ADMIN-03',
      permission: 'hiring_setup',
      icon: ClipboardList,
      keywords: ['plan', 'rounds', 'panel', 'interview', 'template'],
      run: () => void router.navigate({ to: '/admin/interview-plans' }),
    },
    {
      id: 'jobs.career-site',
      label: 'Career site settings',
      section: 'navigate',
      context: 'Admin · ADMIN-12',
      permission: 'hiring_setup',
      icon: Globe,
      keywords: ['career', 'site', 'branding', 'logo', 'public', 'name matching', 'duplicate'],
      run: () => void router.navigate({ to: '/admin/career-site' }),
    },
    {
      // F6.05: "any staff", so no permission object.
      id: 'jobs.refer',
      label: 'Refer someone',
      section: 'navigate',
      context: 'Referral links',
      icon: Link2,
      keywords: ['refer', 'referral', 'friend', 'link', 'share'],
      run: () => void router.navigate({ to: '/referrals' }),
    },
  ];
  return registerCommands(commands);
}
